/*
 * TICK-140 — the glyphs the shared components need, in the stroke weight the console already draws
 * (1.9 at 17px, matching the sidebar icons in `Admin.tsx`).
 *
 * They exist because several components use colour to reinforce a state and are required not to use
 * colour *alone* — an alert's tone, a feedback panel's kind and a field's error all need a shape as
 * well. `aria-hidden` on every one: the sentence beside it is the accessible name, and a decorative
 * glyph announced twice is worse than one announced never.
 */
import type { ReactNode } from 'react';

function Glyph({ size = 16, children }: { size?: number; children: ReactNode }) {
  return (
    <svg
      width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false"
    >
      {children}
    </svg>
  );
}

export const IconInfo = (p: { size?: number }) => (
  <Glyph {...p}><circle cx="12" cy="12" r="9" /><path d="M12 11v5" /><path d="M12 8h.01" /></Glyph>
);
export const IconSuccess = (p: { size?: number }) => (
  <Glyph {...p}><circle cx="12" cy="12" r="9" /><path d="m8.5 12.5 2.5 2.5 4.5-5" /></Glyph>
);
export const IconWarning = (p: { size?: number }) => (
  <Glyph {...p}><path d="M10.3 3.9 2.4 17.4a2 2 0 0 0 1.7 3h15.8a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" /><path d="M12 9v4" /><path d="M12 17h.01" /></Glyph>
);
export const IconDanger = (p: { size?: number }) => (
  <Glyph {...p}><circle cx="12" cy="12" r="9" /><path d="m15 9-6 6" /><path d="m9 9 6 6" /></Glyph>
);
export const IconEmpty = (p: { size?: number }) => (
  <Glyph {...p}><path d="M3 7.5 12 3l9 4.5v9L12 21l-9-4.5Z" /><path d="M3 7.5 12 12l9-4.5" /><path d="M12 12v9" /></Glyph>
);
export const IconSearch = (p: { size?: number }) => (
  <Glyph {...p}><circle cx="11" cy="11" r="7" /><path d="m20 20-3.2-3.2" /></Glyph>
);
export const IconLock = (p: { size?: number }) => (
  <Glyph {...p}><rect x="4" y="10" width="16" height="10" rx="2" /><path d="M8 10V7a4 4 0 0 1 8 0v3" /></Glyph>
);
export const IconEye = (p: { size?: number }) => (
  <Glyph {...p}><path d="M2 12s3.6-6 10-6 10 6 10 6-3.6 6-10 6-10-6-10-6Z" /><circle cx="12" cy="12" r="2.6" /></Glyph>
);
export const IconEyeOff = (p: { size?: number }) => (
  <Glyph {...p}><path d="M10.6 6.2A9.8 9.8 0 0 1 12 6c6.4 0 10 6 10 6a17 17 0 0 1-3.2 3.8" /><path d="M6.3 8.2A17 17 0 0 0 2 12s3.6 6 10 6a9.7 9.7 0 0 0 3.8-.7" /><path d="m3 3 18 18" /></Glyph>
);
export const IconRetry = (p: { size?: number }) => (
  <Glyph {...p}><path d="M21 12a9 9 0 1 1-2.6-6.4" /><path d="M21 4v5h-5" /></Glyph>
);
export const IconChevronDown = (p: { size?: number }) => (
  <Glyph {...p}><path d="m6 9 6 6 6-6" /></Glyph>
);
export const IconChevronLeft = (p: { size?: number }) => (
  <Glyph {...p}><path d="m15 5-7 7 7 7" /></Glyph>
);
export const IconChevronRight = (p: { size?: number }) => (
  <Glyph {...p}><path d="m9 5 7 7-7 7" /></Glyph>
);
export const IconCopy = (p: { size?: number }) => (
  <Glyph {...p}><rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></Glyph>
);
export const IconCalendar = (p: { size?: number }) => (
  <Glyph {...p}><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" /></Glyph>
);
